import { Injectable } from '@angular/core';
import { SymbolType } from '../../../../../domain/symbol.domain';
import { MarketTypeHelper } from '../../../../../domain/market.domain';
import { TradingAccount } from '../../../../../domain/account.domain';
import { TimezoneService } from '../../../../../services/date/timezone.service';
import { DateService } from '../../../../../services/date/date.service';

import {
  EquityOrderAction,
  MultiLegOrderDayType,
  OptionOrderAction,
  OrderDateType,
  OrderItem,
  Strategy
} from '../../../domain';
import {
  MultilegFormModel,
  MultilegItemFormModel,
  OptionSymbolUrlParam,
  OrderFormParameters
} from '../../domain/order-entry.domain';
import { OrderEntryUtilityService } from '../order-entry-utility.service';
import { CreateOrderFormService } from './create-order-form.service';

@Injectable()
export class CreateMloOrderFormService extends CreateOrderFormService<MultilegFormModel> {

  constructor(private timezoneService: TimezoneService) {
    super();
  }

  /**
   * Initialize object that represent state of UI across multiple steps
   */
  initializeViewModel(_selectedAccount?: TradingAccount, orderFormParameters?: OrderFormParameters): MultilegFormModel {
    this.initFormModel();
    if (orderFormParameters !== undefined &&
      orderFormParameters.orderStateParameters !== undefined &&
      orderFormParameters.orderStateParameters.orderKey !== undefined) {
      //   // its copy order, load object
      const orderTerms = orderFormParameters.orderDetails.orderTerms;

      const [firstLeg, secondLeg]: Array<OrderItem> = orderTerms.order;
      this.temp.strategyType = Strategy.MLO;
      this.temp.selectedType = firstLeg.security.type as SymbolType.OPTION | SymbolType.EQUITY;
      this.temp.pricePlan = orderTerms.pricePlan;
      this.temp.goodTill = orderTerms.multilegEntry.extendedTerms.goodTill as MultiLegOrderDayType;
      this.temp.goodTillDate = orderTerms.multilegEntry.extendedTerms.goodTillDate;
      this.temp.limitPrice = orderTerms.multilegEntry.extendedTerms.limitPrice;

      const secondLegOptionValues = this.getOptionValues(secondLeg.security.displayTicker);
      const firstLegOptionValues = this.temp.selectedType === SymbolType.OPTION ?
        this.getOptionValues(firstLeg.security.displayTicker) : undefined;

      this.temp.firstLeg = this.getMultilegItem({
        orderAction: firstLeg.extendedTerms.orderAction as EquityOrderAction | OptionOrderAction,
        orderQuantity: firstLeg.quantity,
        optionPrepopulateValues: firstLegOptionValues
      });
      this.temp.secondLeg = this.getMultilegItem({
        orderAction: secondLeg.extendedTerms.orderAction as EquityOrderAction | OptionOrderAction,
        orderQuantity: secondLeg.quantity,
        optionPrepopulateValues: secondLegOptionValues
      });
      this.setOrderSymbol(
        firstLeg.security.displayTicker,
        firstLeg.security.market
      );
    }
    return Object.assign({}, this.temp); // TODO double check
  }

  getTodaysDate(): Date {
    return DateService.stripTime(this.timezoneService.getCurrentDateInServerTimezone());
  }

  isFutureOptionDate(expiryDate: Date): boolean {
    const today = this.getTodaysDate();
    return DateService.getUTCTimeDifferenceInMilliseconds(today, expiryDate) >= 0;
  }

  getAllowedOptionActionTypes(optionCode: number): Array<OptionOrderAction> {
    switch (optionCode) {
      case 1: // TODO get rid of magic numbers
        return [
          OptionOrderAction.BUY_TO_OPEN,
          OptionOrderAction.SELL_TO_CLOSE];
      case 2:
      case 3:
        return [
          OptionOrderAction.BUY_TO_OPEN,
          OptionOrderAction.SELL_TO_OPEN_COVERED,
          OptionOrderAction.BUY_TO_CLOSE,
          OptionOrderAction.SELL_TO_CLOSE];
      case 4:
        return [
          OptionOrderAction.BUY_TO_OPEN,
          OptionOrderAction.SELL_TO_OPEN_COVERED,
          OptionOrderAction.SELL_TO_OPEN_UNCOVERED,
          OptionOrderAction.BUY_TO_CLOSE,
          OptionOrderAction.SELL_TO_CLOSE];
      default:
        return [];
    }
  }

  getAllowedEquityActionTypes(isShortMarginAccount: boolean): Array<EquityOrderAction> {
    return isShortMarginAccount ?
      [EquityOrderAction.BUY_TO_COVER, EquityOrderAction.SHORT_SELL] :
      [EquityOrderAction.BUY, EquityOrderAction.SELL];
  }

  private getMultilegItem(prepopulate?: Pick<MultilegItemFormModel,
  'orderQuantity' | 'optionPrepopulateValues' | 'orderAction'>): MultilegItemFormModel {
    return Object.assign({
      optionDetails: undefined,
      optionQuoteParams: undefined,
      orderAction: undefined,
      orderQuantity: undefined,
      quote: undefined,
      optionPrepopulateValues: undefined,
      underlyingType: undefined,
      underlyingTicker: undefined
    }, prepopulate);
  }

  private setOrderSymbol(symbolString: string, market: string): void {
    const optionValues = OrderEntryUtilityService.parseOptionUrlParams(symbolString);
    const symbol = optionValues.underlyingSymbol;
    const symbolType = SymbolType.OPTION;
    if (symbol !== undefined &&
        market !== undefined &&
        symbolType !== undefined) {
      this.temp.selectedSymbol = {
        symbol,
        market: MarketTypeHelper.fromString(market),
        type: SymbolType.OPTION,
        symbolType: SymbolType.OPTION_UNDERLYING
      };
    }
  }

  private getOptionValues(displayTicker: string): OptionSymbolUrlParam {
    return OrderEntryUtilityService.parseOptionUrlParams(displayTicker);
  }

  private initFormModel(): void {
    this.temp = {
      strategyType: Strategy.MLO,
      selectedType: SymbolType.OPTION,
      selectedSymbol: undefined,
      firstLeg: {
        optionQuoteParams: undefined,
        optionDetails: undefined,
        orderAction: undefined,
        orderQuantity: undefined
      },
      secondLeg: {
        optionQuoteParams: undefined,
        optionDetails: undefined,
        orderAction: undefined,
        orderQuantity: undefined
      },
      goodTill: OrderDateType.DAY,
      goodTillDate: undefined,
      pricePlan: undefined,
      limitPrice: undefined
    } as MultilegFormModel;
  }
}

